/*
 * Creative Analysis Overview — the numbers (2026-10-28, plan
 * 2026-10-28-001 PR 2). Pure functions over one product's snapshot so the
 * React layer only draws.
 *
 * Money rules (Ben): spend is Meta. Revenue and conversions are FunnelFlux
 * when the snapshot has FF money, Meta otherwise; the two are never mixed,
 * and every label says which. Prospecting and retargeting are reported
 * separately; Prospecting is the default.
 *
 * Trend lines: Meta metrics get a daily line per segment (retargeting days
 * come from a campaign-or-ad-set filter; prospecting = total − retargeting).
 * FunnelFlux has no per-segment day split, so FF-sourced lines appear on the
 * All segment only; per segment those tiles show the window value with the
 * change against the prior window. FF's last two days can still be catching
 * up, so they are drawn as provisional.
 */
import { applyFfMoney } from "./explorer";
import type {
  CoverageDaily,
  CoverageDailyMeta,
  CoverageLivePriorRecord,
  CoverageLiveRecord,
  CoverageProduct,
} from "./types";

export const WINDOWS = [3, 7, 14, 28] as const;
export type WindowDays = (typeof WINDOWS)[number];
export type Segment = "pros" | "ret" | "all";

export const SEGMENT_LABEL: Record<Segment, string> = {
  pros: "Prospecting",
  ret: "Retargeting",
  all: "All",
};

/** Days of FunnelFlux data treated as still catching up. */
export const FF_PROVISIONAL_DAYS = 2;

export function parseWindow(v: string | null | undefined): WindowDays {
  const n = Number(v);
  return (WINDOWS as readonly number[]).includes(n) ? (n as WindowDays) : 28;
}

export function parseSegment(v: string | null | undefined): Segment {
  return v === "ret" || v === "all" ? v : "pros";
}

/** Windows this snapshot can show: 28 always, the short ones when present. */
export function availableWindows(p: CoverageProduct): WindowDays[] {
  return WINDOWS.filter((w) => w === 28 || p.windows?.[String(w) as "3" | "7" | "14"] !== undefined);
}

/**
 * The product as seen through a 3/7/14-day window: each window row borrows
 * its name, dims, segment, link and thumbnail from the 28-day record it
 * indexes, with the window's own money. 28 (or a window the snapshot lacks)
 * returns the product unchanged. Raw money in, raw money out — FunnelFlux
 * swapping happens later (applyFfMoney).
 */
export function productForWindow(p: CoverageProduct, w: WindowDays): CoverageProduct {
  if (w === 28) return p;
  const win = p.windows?.[String(w) as "3" | "7" | "14"];
  if (!win) return p;
  const live: CoverageLiveRecord[] = [];
  for (const r of win.live) {
    const b = p.live[r.i];
    if (!b) continue;
    live.push({ ...b, sp: r.sp, val: r.val, pu: r.pu, im: r.im, lc: r.lc, fv: r.fv, fpu: r.fpu });
  }
  const livePrior: CoverageLivePriorRecord[] = [];
  for (const r of win.live_prior) {
    const b = p.live[r.i];
    if (!b) continue;
    livePrior.push({ d: b.d, ck: b.ck, rt: b.rt, sp: r.sp, val: r.val, pu: r.pu, fv: r.fv, fpu: r.fpu, im: r.im, lc: r.lc });
  }
  const cold = live.filter((r) => !r.rt);
  const coldSp = cold.reduce((a, r) => a + r.sp, 0);
  const coldVal = cold.reduce((a, r) => a + r.val, 0);
  return {
    ...p,
    live,
    live_prior: livePrior,
    base: { roas: coldSp ? coldVal / coldSp : 0, spend: coldSp },
    meta: { ...p.meta, start: win.start, end: win.end, prior_start: win.prior_start, prior_end: win.prior_end },
  };
}

type MoneyRec = CoverageLivePriorRecord;

export interface Totals {
  sp: number;
  /** Revenue: FunnelFlux when the product's money is FF, else Meta. */
  val: number;
  pu: number;
  /** Meta's own revenue / purchases (the cross-check). */
  mval: number;
  mpu: number;
  im: number;
  lc: number;
}

const inSegment = (r: { rt?: boolean }, seg: Segment): boolean =>
  seg === "all" ? true : seg === "ret" ? r.rt === true : r.rt !== true;

export function segmentTotals(recs: readonly MoneyRec[], seg: Segment): Totals {
  const t: Totals = { sp: 0, val: 0, pu: 0, mval: 0, mpu: 0, im: 0, lc: 0 };
  for (const r of recs) {
    if (!inSegment(r, seg)) continue;
    t.sp += r.sp;
    t.val += r.val;
    t.pu += r.pu;
    t.mval += r.mval ?? r.val;
    t.mpu += r.mpu ?? r.pu;
    t.im += r.im ?? 0;
    t.lc += r.lc ?? 0;
  }
  return t;
}

const div = (a: number, b: number): number | null => (b > 0 ? a / b : null);

export type TileFormat = "money" | "money2" | "ratio" | "count";

export interface TileSpark {
  cur: Array<number | null>;
  prior: Array<number | null>;
  /** Trailing points drawn as provisional (FunnelFlux catching up). */
  provisional: number;
}

export interface Tile {
  id: string;
  label: string;
  value: number | null;
  prior: number | null;
  format: TileFormat;
  /** Which direction is good; null = neutral (spend is volume, not quality). */
  better: "up" | "down" | null;
  spark: TileSpark | null;
  /** FunnelFlux-sourced (provisional tail, early-read rules). */
  ff: boolean;
}

export interface OverviewModel {
  money: "ff" | "meta";
  window: { start: string; end: string; prior_start?: string; prior_end?: string };
  tiles: Tile[];
  /** True when the segment's Meta trend lines are available. */
  segmentDaily: boolean;
}

/** The daily series sliced to a window's current and prior days, or null. */
function sliceDays(d: CoverageDaily | undefined, end: string, w: number): { cur: number[]; prior: number[] } | null {
  if (!d || d.dates.length < 2 * w) return null;
  const last = d.dates.lastIndexOf(end);
  if (last < 2 * w - 1) return null;
  const cur = Array.from({ length: w }, (_, k) => last - w + 1 + k);
  const prior = Array.from({ length: w }, (_, k) => last - 2 * w + 1 + k);
  return { cur, prior };
}

function segDaily(d: CoverageDaily, seg: Segment): CoverageDailyMeta | null {
  if (seg === "all") return d.total;
  if (!d.rt) return null;
  if (seg === "ret") return d.rt;
  const sub = (k: keyof CoverageDailyMeta) => d.total[k].map((v, i) => v - (d.rt![k][i] ?? 0));
  return { sp: sub("sp"), im: sub("im"), lc: sub("lc"), mval: sub("mval"), mpu: sub("mpu") };
}

/**
 * Overview tiles for one product, window and segment. The current and prior
 * values come from the window's ad records (exact per segment); trend lines
 * come from the daily series where it supports the segment.
 */
export function overviewModel(raw: CoverageProduct, w: WindowDays, seg: Segment): OverviewModel {
  const p = applyFfMoney(productForWindow(raw, w));
  const ff = p.money === "ff";
  const cur = segmentTotals(p.live, seg);
  const pri = segmentTotals(p.live_prior ?? [], seg);

  const idx = sliceDays(raw.daily, p.meta.end, w);
  const metaDays = raw.daily ? segDaily(raw.daily, seg) : null;
  const ffDays = ff && seg === "all" ? (raw.daily?.ff ?? null) : null;
  const totalDays = raw.daily?.total ?? null;

  const spark = (
    f: ((i: number) => number | null) | null,
    provisional = 0,
  ): TileSpark | null =>
    idx && f ? { cur: idx.cur.map(f), prior: idx.prior.map(f), provisional } : null;
  const m = metaDays;
  const metaSpark = (f: (s: CoverageDailyMeta, i: number) => number | null) =>
    spark(m ? (i) => f(m, i) : null);
  const ffSpark = (f: (i: number) => number | null) =>
    spark(ffDays && totalDays ? f : null, FF_PROVISIONAL_DAYS);

  const src = ff ? "FF" : "Meta";
  const tiles: Tile[] = [
    {
      id: "spend", label: "Spend (Meta)", value: cur.sp, prior: pri.sp, format: "money", better: null, ff: false,
      spark: metaSpark((s, i) => s.sp[i] ?? null),
    },
    {
      id: "revenue", label: `Revenue (${src})`, value: cur.val, prior: pri.val, format: "money", better: "up", ff,
      spark: ff ? ffSpark((i) => ffDays!.rev[i] ?? null) : metaSpark((s, i) => s.mval[i] ?? null),
    },
    {
      id: "roas", label: `ROAS (${src})`, value: div(cur.val, cur.sp), prior: div(pri.val, pri.sp), format: "ratio", better: "up", ff,
      spark: ff
        ? ffSpark((i) => div(ffDays!.rev[i] ?? 0, totalDays!.sp[i] ?? 0))
        : metaSpark((s, i) => div(s.mval[i] ?? 0, s.sp[i] ?? 0)),
    },
    {
      id: "cpa", label: `CPA (${src})`, value: div(cur.sp, cur.pu), prior: div(pri.sp, pri.pu), format: "money2", better: "down", ff,
      spark: ff
        ? ffSpark((i) => div(totalDays!.sp[i] ?? 0, ffDays!.conv[i] ?? 0))
        : metaSpark((s, i) => div(s.sp[i] ?? 0, s.mpu[i] ?? 0)),
    },
    {
      id: "aov", label: `AOV (${src})`, value: div(cur.val, cur.pu), prior: div(pri.val, pri.pu), format: "money2", better: "up", ff,
      spark: ff
        ? ffSpark((i) => div(ffDays!.rev[i] ?? 0, ffDays!.conv[i] ?? 0))
        : metaSpark((s, i) => div(s.mval[i] ?? 0, s.mpu[i] ?? 0)),
    },
    {
      id: "conv", label: ff ? "Conversions (FF)" : "Purchases (Meta)", value: cur.pu, prior: pri.pu, format: "count", better: "up", ff,
      spark: ff ? ffSpark((i) => ffDays!.conv[i] ?? null) : metaSpark((s, i) => s.mpu[i] ?? null),
    },
    {
      id: "cpc", label: "CPC link (Meta)", value: div(cur.sp, cur.lc), prior: div(pri.sp, pri.lc), format: "money2", better: "down", ff: false,
      spark: metaSpark((s, i) => div(s.sp[i] ?? 0, s.lc[i] ?? 0)),
    },
    {
      id: "cpm", label: "CPM (Meta)", value: cur.im ? (cur.sp / cur.im) * 1000 : null, prior: pri.im ? (pri.sp / pri.im) * 1000 : null,
      format: "money2", better: "down", ff: false,
      spark: metaSpark((s, i) => ((s.im[i] ?? 0) > 0 ? ((s.sp[i] ?? 0) / s.im[i]!) * 1000 : null)),
    },
  ];
  if (ff) {
    tiles.push({
      id: "mroas", label: "Meta ROAS (cross-check)", value: div(cur.mval, cur.sp), prior: div(pri.mval, pri.sp),
      format: "ratio", better: "up", ff: false,
      spark: metaSpark((s, i) => div(s.mval[i] ?? 0, s.sp[i] ?? 0)),
    });
  }
  return {
    money: ff ? "ff" : "meta",
    window: { start: p.meta.start, end: p.meta.end, prior_start: p.meta.prior_start, prior_end: p.meta.prior_end },
    tiles,
    segmentDaily: m !== null && idx !== null,
  };
}

/** Change vs prior as a fraction (0.12 = +12%), or null when not comparable. */
export function change(t: Pick<Tile, "value" | "prior">): number | null {
  if (t.value === null || t.prior === null || t.prior === 0) return null;
  return (t.value - t.prior) / Math.abs(t.prior);
}

/** Whether a change reads good, bad or neutral for the tile. */
export function changeTone(t: Tile, w: WindowDays): "good" | "bad" | "neutral" {
  const c = change(t);
  // A 3-day FunnelFlux read is two-thirds provisional: never colour it.
  if (c === null || t.better === null || (t.ff && w === 3) || Math.abs(c) < 0.02) return "neutral";
  const up = c > 0;
  return (t.better === "up") === up ? "good" : "bad";
}

export interface FamilyRow {
  key: string;
  label: string;
  sp: number;
  roas: number;
}

/**
 * Concept families ranked by ROAS for the window and segment, among
 * families with real spend (at least $150 and 2% of the segment's spend),
 * best five and worst five. ROAS uses the product's revenue source.
 */
export function familyRanking(raw: CoverageProduct, w: WindowDays, seg: Segment): { top: FamilyRow[]; bottom: FamilyRow[] } {
  const p = applyFfMoney(productForWindow(raw, w));
  const groups = new Map<string, { label: string; sp: number; val: number }>();
  let segSp = 0;
  for (const r of p.live) {
    if (!inSegment(r, seg)) continue;
    segSp += r.sp;
    const key = r.ck || r.d.concept || r.lb;
    if (!key) continue;
    const g = groups.get(key) ?? { label: r.d.concept || r.ck || r.lb, sp: 0, val: 0 };
    g.sp += r.sp;
    g.val += r.val;
    groups.set(key, g);
  }
  const floor = Math.max(150, segSp * 0.02);
  const ranked = [...groups.entries()]
    .filter(([, g]) => g.sp >= floor)
    .map(([key, g]) => ({ key, label: g.label, sp: g.sp, roas: g.val / g.sp }))
    .sort((a, b) => b.roas - a.roas);
  const top = ranked.slice(0, 5);
  const bottom = ranked.length > 5 ? ranked.slice(Math.max(5, ranked.length - 5)).reverse() : [];
  return { top, bottom };
}
